/**
 * The `TokenStore` the shared `@cyngn/auth-react` provider reads and writes.
 *
 * The session lives under the library's `cyngn_auth.*` keys (`LocalStorageTokenStore`); the
 * app's own `JWT` / `cognito_*` keys are legacy (DEV-6053). A browser that still holds a legacy
 * session is migrated once, at module load and before any provider or axios client reads the
 * store, so the session is never silently unauthenticated mid-flight:
 *
 * * a legacy Cognito session (`cognito_id_token` + `cognito_refresh_token`) is copied into the
 *   `cyngn_auth.*` keys, so the user stays signed in;
 * * a legacy Django JWT session (`JWT` with no ID token) has no Cognito refresh token and cannot
 *   be continued by the shared provider, so it is cleared and the user is sent back to login.
 *
 * Either way the legacy keys are removed, which makes the migration idempotent and means a
 * rollback (revert of the PR) restarts from a clean login rather than a half-copied session.
 */
import {
  LocalStorageTokenStore,
  setSharedTokenStore,
  type TokenListener,
  type TokenSet,
  type TokenStore,
} from "@cyngn/auth-react";

import { LEGACY_AUTH_STORAGE_KEYS } from "./cognito";

const BEARER_PREFIX = "Bearer ";

const withoutBearerPrefix = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  return value.startsWith(BEARER_PREFIX)
    ? value.slice(BEARER_PREFIX.length)
    : value;
};

export type LegacySessionMigration =
  | "none"
  | "migrated"
  | "signed_out"
  | "skipped";

export const clearLegacyAuthKeys = (storage: Storage): void => {
  for (const key of Object.values(LEGACY_AUTH_STORAGE_KEYS)) {
    storage.removeItem(key);
  }
};

const hasLegacyKeys = (storage: Storage): boolean =>
  Object.values(LEGACY_AUTH_STORAGE_KEYS).some(
    (key) => storage.getItem(key) !== null
  );

/**
 * One-time move of a legacy-keyed session onto the shared store. Returns what happened so the
 * caller can log it; safe to call on every load.
 *
 * `skipped`: the shared store already holds a session, so the legacy keys are only removed —
 * a session the library owns is never overwritten by stale legacy copies.
 */
export const migrateLegacySession = (
  store: TokenStore,
  storage: Storage = window.localStorage
): LegacySessionMigration => {
  if (!hasLegacyKeys(storage)) {
    return "none";
  }

  if (store.read().idToken) {
    clearLegacyAuthKeys(storage);
    return "skipped";
  }

  const idToken = storage.getItem(LEGACY_AUTH_STORAGE_KEYS.COGNITO_ID_TOKEN);
  const refreshToken = storage.getItem(
    LEGACY_AUTH_STORAGE_KEYS.COGNITO_REFRESH_TOKEN
  );
  const expiresAt = Number(
    storage.getItem(LEGACY_AUTH_STORAGE_KEYS.COGNITO_EXPIRES_AT)
  );

  if (!idToken || !refreshToken) {
    // A Django JWT login, or a Cognito session that can no longer be refreshed: forced re-login.
    clearLegacyAuthKeys(storage);
    store.clear();
    return "signed_out";
  }

  const tokens: TokenSet = {
    idToken,
    accessToken: withoutBearerPrefix(
      storage.getItem(LEGACY_AUTH_STORAGE_KEYS.JWT)
    ),
    refreshToken,
    // An unreadable expiry is treated as already expired so the provider refreshes on bootstrap.
    expiresAt: Number.isFinite(expiresAt) && expiresAt > 0 ? expiresAt : 0,
  };
  store.write(tokens);
  clearLegacyAuthKeys(storage);
  return "migrated";
};

/**
 * The library store plus cross-tab awareness: another tab logging in or out changes the
 * session for this one too, but only while someone is listening — a store nobody subscribes to
 * must not keep itself alive on the window. Composed rather than subclassed: the app compiles to
 * ES5, which cannot extend a native class.
 */
export class AppTokenStore implements TokenStore {
  private readonly listeners = new Set<TokenListener>();

  constructor(
    private readonly inner: LocalStorageTokenStore = new LocalStorageTokenStore()
  ) {}

  read(): TokenSet {
    return this.inner.read();
  }

  write(tokens: TokenSet): void {
    this.inner.write(tokens);
    this.emit();
  }

  clear(): void {
    this.inner.clear();
    this.emit();
  }

  bearerToken(): string | null {
    return this.inner.bearerToken();
  }

  subscribe(listener: TokenListener): () => void {
    if (this.listeners.size === 0) {
      window.addEventListener("storage", this.onStorage);
    }
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) {
        window.removeEventListener("storage", this.onStorage);
      }
    };
  }

  private readonly onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key.startsWith("cyngn_auth.")) {
      this.emit();
    }
  };

  private emit(): void {
    const tokens = this.read();
    this.listeners.forEach((listener) => listener(tokens));
  }
}

export const appTokenStore = new AppTokenStore();

const migration = migrateLegacySession(appTokenStore);
if (migration !== "none") {
  console.info("[auth] legacy session %s", migration);
}

// The axios clients are created at import time, before any provider mounts, so the library's
// interceptor has to be able to find the store without React in scope.
setSharedTokenStore(appTokenStore);
